import { useCallback } from "react";
import { usePermissions } from "@/hooks/usePermissions";

/** Territory of a request (null while it is being created) */
interface RequestTerritory {
  district_id: string | null;
  region_id: string | null;
}

export interface RequestPermissions {
  canApprove: boolean;
  canReject: boolean;
  canMarkPrinted: boolean;
  canMarkDelivered: boolean;
  /**
   * Reviewer powers that are not a workflow step: assigning/editing the code and editing a
   * request after it was submitted. Granted with credential_request.approve.
   */
  canReview: boolean;
}

/**
 * What the current user may do with credential requests. Per request, permissions follow the
 * request's own district / region (same rule as the database policies).
 */
export function useCredentialPermissions() {
  const { can, canAny, isLoading } = usePermissions();

  /** Without a request (new one) the permission is checked at any scope */
  const forRequest = useCallback(
    (request?: RequestTerritory | null): RequestPermissions => {
      const target = request ? { districtId: request.district_id, regionId: request.region_id } : undefined;
      const canApprove = can("credential_request.approve", target);
      return {
        canApprove,
        canReject: can("credential_request.reject", target),
        canMarkPrinted: can("credential_request.mark_printed", target),
        canMarkDelivered: can("credential_request.mark_delivered", target),
        canReview: canApprove,
      };
    },
    [can],
  );

  return {
    forRequest,
    isLoading,
    /** Show the workflow actions column in lists */
    canWorkflowAnywhere: canAny(
      "credential_request.approve",
      "credential_request.reject",
      "credential_request.mark_printed",
      "credential_request.mark_delivered",
    ),
    /** Sync function-credential ministers from the current authorities */
    canSyncAuthorities: can("credential_request.edit"),
  };
}
